import './Networking.css'
import NetworkingOpt from "../../components/seo/NetworkingOpt";

export default function NetworkAutomation() {
    return (
        <>
            <NetworkingOpt
                title="Network Automation"
                description="Study network automation concepts including data, control, and management planes, SDN, APIs, JSON, and automation benefits."
                keywords="network automation, sdn, api, json, management plane, control plane, data plane"
            />

            <article className='content'>
                <h1 style={{display:'flex', justifyContent:'center' }}>Network Automation</h1>
                
                <h2>Introduction</h2>
                
                <p>Traditional networks: devices managed individually via CLI (SSH).</p>
                <h3>Benefits of automation:</h3>
                <p>Reduces human error (typos, misconfigurations).</p>
                <p>Enables scalability and faster deployments.</p>
                <p>Ensures network‑wide policy compliance (standard configs, versions).</p>
                <p>Lowers operational expenses (OPEX).</p>
                <p>Common tools: SDN, Ansible, Puppet, Python scripts.</p>
                
                <h3>Router Functions </h3>
                <p>Forwards packets between networks (Layer 3).</p>
                <p>Uses OSPF to share routes and build routing tables.</p>
                <p>Uses ARP to map IP → MAC addresses.</p>
                <p>Logs events via Syslog.</p>
                <p>Managed remotely via SSH.</p>
                
                <h3>Switch Functions </h3>
                <p>Forwards frames within a LAN (Layer 2).</p>
                <p>Uses STP to prevent loops.</p>
                <p>Builds MAC address table from source MACs.</p>
                <p>Logs events via Syslog.</p>
                <p>Managed via SSH.</p>
                
                <h2>Logical Planes</h2>
                
                <h3>Data Plane (Forwarding Plane) </h3>
                
                
                <table>
                    <tr><td>Function</td><td>Description</td><td>Common Protocols</td><td>Port Numbers</td></tr>
                    <tr><td>Packet forwarding</td><td>Moves user data packets between interfaces based on routing decisions.</td><td>Ethernet, IP, MPLS</td><td>N/A (Layer 2/3 protocols)</td></tr>
                    <tr><td>Transport of payloads</td><td>Carries actual application data (voice, video, files).</td><td>TCP, UDP</td><td>TCP/UDP ports vary by application (e.g., HTTP 80, HTTPS 443)</td></tr>
                    <tr><td>Encapsulation &amp; switching</td><td>Handles frame/packet encapsulation and switching logic.</td><td>ARP, ICMP</td><td>ICMP uses no ports (protocol number 1)</td></tr>
                    <tr><td>User data requests</td><td>Translating domain names to IP addresses. </td><td>DNS</td><td>DNS: UDP/TCP 53</td></tr>
                </table>
                
                <p>Handles user traffic forwarding between interfaces.</p>
                <p>Router: looks up routing table, re‑encapsulates frames.</p>
                <p>Switch: looks up destination MAC, forwards or floods.</p>
                <h4>Includes:</h4>
                <p>VLAN tagging (802.1Q).</p>
                <p>NAT (address translation).</p>
                <p>ACLs, port security decisions.</p>
                <p>Uses ASIC hardware and TCAM/CAM memory for fast lookups.</p>
                
                <h4>Data Plane Hardware:</h4>
                <p><em>ASIC (Application‑Specific Integrated Circuit)</em></p>
                <p>Specialized hardware chip designed for high‑speed packet forwarding.</p>
                <p>Handles switching and routing logic independently of the CPU.</p>
                <p><em> 	TCAM / CAM Memory</em></p>
                <table>
                    <tr><td>Component</td><td>Description</td></tr>
                    <tr><td>TCAM (Ternary Content‑Addressable Memory)</td><td>Stores MAC address table entries for fast lookups.</td></tr>
                    <tr><td>CAM (Content‑Addressable Memory)</td><td>Another term often used for the MAC address table.</td></tr>
                </table>
                <p><em>Example: Switch Operation</em></p>
                <p>Frame Reception: ASIC receives the incoming frame.</p>
                <p>Lookup: ASIC sends the destination MAC address to TCAM.</p>
                <p>Match: TCAM returns the corresponding MAC table entry.</p>
                <p>Forwarding: Frame is sent out through the correct interface.</p>
                
                 
                <h3>Control Plane</h3>
                
                <table>
                    <tr><td>Function</td><td>Description</td><td>Common Protocols</td><td>Port Numbers</td></tr>
                    <tr><td>Routing, Switching &amp; signaling</td><td>Determines best paths and maintains routing tables.</td><td>OSPF, BGP, EIGRP, RIP, STP</td><td>OSPF: IP protocol 89 BGP: TCP 179 <br/>EIGRP: IP protocol 88 RIP: UDP 520<br/>STP: BPDU(802.1D)</td></tr>
                    <tr><td>Session setup &amp; remote control</td><td>Establishes and manages sessions between devices.</td><td>RDP</td><td>RDP: TCP 3389</td></tr>
                    <tr><td>Neighbor discovery &amp; link state updates</td><td>Exchanges control messages to maintain topology awareness.</td><td>ICMP, LLDP, ARP</td><td>ICMP: IP protocol 1 <br/>LLDP: Ethernet type 0x88CC<br/>ARP: EtherType 0x0806</td></tr>
                </table>
                            
                <p>Builds and maintains tables used by the data plane:</p>
                <p>Routing (OSPF, BGP, RIP).</p>
                <p>MAC, ARP, STP tables.</p>
                <p>Performs overhead work (not user data forwarding).</p>

                <h4>Examples:</h4>
                <p>OSPF calculates routes.</p>
                <p>STP decides which ports forward frames.</p>
                <p>ARP builds mappings for forwarding decisions.</p>
                
                <h3>Management Plane</h3>
                
                <table>
                    <tr><td>Function</td><td>Description</td><td>Common Protocols</td><td>Port Numbers</td></tr>
                    <tr><td>Device configuration &amp; monitoring</td><td>Allows administrators to manage and monitor devices.</td><td>SNMP, HTTP, HTTPS</td><td>SNMP: UDP 161 HTTP: TCP 80 HTTPS: TCP 443</td></tr>
                    <tr><td>Remote administration</td><td>Provides secure access for configuration and troubleshooting.</td><td>SSH, Telnet</td><td>SSH: TCP 22 Telnet: TCP 23</td></tr>
                    <tr><td>Logging &amp; reporting</td><td>Collects system logs and performance metrics.</td><td>Syslog, NetFlow</td><td>Syslog: UDP 514 NetFlow: UDP 2055 (default)</td></tr>
                </table>
                
                
                <p>Used for device configuration and monitoring.</p>
                <p>Performs overhead work — not directly involved in forwarding user traffic. </p>
                <p>Operates via CPU (not ASIC).</p> 

                <h4><em>Protocols:</em></h4>
                <p>SSH/Telnet → CLI access.</p>
                <p>Syslog → event logging.</p>
                <p>SNMP → performance monitoring.</p>
                <p>NTP → time synchronization.</p>           
                


                <h2>Software Defined Networking</h2>

                <p>Centralizes the control plane into a controller.</p>
                <p>Traditional networks: distributed control plane.</p>
                <p>Controller communicates via APIs.</p>
                
                <h3>Southbound Interface (SBI)</h3>
                <p>Between controller and network devices.</p>
                <p>Enables device control and data exchange.</p>
                <p>Examples: OpenFlow, Cisco OpFlex, Cisco onePK, NETCONF.</p>

                <h3>Northbound Interface (NBI)</h3>

                <p>Between controller and applications.</p>
                <p>Uses REST API (JSON/XML).</p>
                <p>Allows apps to program network behavior.</p>
                
                <h2>Data Serialization Formats</h2>
                
                
                <table>
                    <tr><td>Format</td><td>Key Traits</td><td>Used By</td></tr>
                    <tr><td>JSON</td><td>Human‑readable, language‑independent, uses { } and [], whitespace insignificant</td><td>REST APIs</td></tr>
                    <tr><td>XML</td><td>Tag‑based &lt;key&gt;value&lt;/key&gt;, less human‑readable</td><td>REST APIs</td></tr>
                    <tr><td>YAML</td><td>Human‑readable, indentation significant, starts with ---, uses Key: Value</td><td>Ansible</td></tr>
                </table>
                
                <h2>REST API</h2>
                <p>API = interface for app‑to‑app communication.</p>
                <p>Used in SDN.</p>
                <p>Stateless: each request independent; server stores no session info.</p>
                <p>Caching: stores data for reuse.</p>
                
                <h3>HTTP Request structure:  </h3>

                <p>https://sandboxdnac.cisco.com/dna/intent/api/v1/...  </p>
                <p>Includes verb, URI, and headers (e.g., Accept).</p>

                <h3>Response codes:</h3>

                <p>1xx Informational</p>
                <p>2xx Success</p>
                <p>3xx Redirection</p>
                <p>4xx Client Error</p>
                <p>5xx Server Error</p>

                <h3>REST Constraints:  </h3>

                <p>Uniform Interface </p>
                <p>Client‑Server </p>
                <p>Stateless </p>
                <p>Cacheable </p>
                <p>Layered System </p>
                <p>Code‑on‑Demand</p>

                
                <h2>Cisco SD‑Access (SDA) </h2>

                <p>Cisco’s SDN solution for campus LAN automation.</p>
                <p>Controller: Cisco DNA Center.</p>
                <p>Other Cisco SDN solutions:</p>
                <p>ACI → Data Center.</p>
                <p>SD‑WAN → WAN automation.</p>
                
                
                <h3>Architecture Layers</h3>
                
                <table>
                    <tr><td>Layer</td><td>Function</td></tr>
                    <tr><td>Application</td><td>Defines desired network behavior</td></tr>
                    <tr><td>Control</td><td>SDN Controller (DNA Center)</td></tr>
                    <tr><td>Infrastructure</td><td>Network devices forwarding traffic</td></tr>
                </table>
                
                <h3>Underlay &amp; Overlay</h3>

                <p>Underlay: physical network (Layer 3, IS‑IS).</p>
                <p>Overlay: virtual network (VXLAN tunnels).</p>
                <p>Fabric: combination of both.</p>

                <h3>Node Roles:</h3>
                
                <table>
                    <tr><td>Role</td><td>Function</td></tr>
                    <tr><td>Edge Node</td><td>Connects end hosts</td></tr>
                    <tr><td>Border Node</td><td>Connects to external networks</td></tr>
                    <tr><td>Control Node</td><td>Uses LISP for control‑plane functions</td></tr>
                </table>
                
                <h3>Deployments:</h3>

                <p>Brownfield → added to existing network.</p>
                <p>Greenfield → DNA Center configures new underlay.</p>
                

                <h2>Configuration Management Tools</h2>
                
                <table>
                    <tr><td>Tool</td><td>Language</td><td>Agent</td><td>Model</td><td>Port</td><td>Key Features</td></tr>
                    <tr><td>Ansible</td><td>Python</td><td>Agentless</td><td>Push</td><td>SSH (22)</td><td>Uses YAML; simple automation</td></tr>
                    <tr><td>Puppet</td><td>Ruby</td><td>Agent‑based</td><td>Pull</td><td>TCP 8140</td><td>Uses manifests/templates</td></tr>
                    <tr><td>Chef</td><td>Ruby (DSL)</td><td>Agent‑based</td><td>Pull</td><td>TCP 10002</td><td>Uses recipes, cookbooks, run‑lists</td></tr>
                </table>
                
                <p>Configuration Drift: deviation from standard configs over time. These tools ensure centralized, consistent configuration across devices.</p>
                
            </article>

        </>
            )
};
